import React, { useMemo } from 'react';
import styled, { useTheme } from 'styled-components';
import { useTranslation } from 'react-i18next';
import {
  PolarAngleAxis, PolarGrid, PolarRadiusAxis, Radar, RadarChart, ResponsiveContainer, Tooltip,
} from 'recharts';

// İK Zekâsı panelindeki kapsam radarı.
//
// ═══════════ NEDEN BU VERİ ═══════════
// Radar, AYNI ÖLÇEKTE birkaç boyut ister. Üründe buna doğal olarak uyan tek şey KATMAN bazlı
// metrik kullanılabilirliği: dört katmanın (Tanımlayıcı / Karşılaştırmalı / Erken Uyarı /
// Öngörü) her biri için "kaç metrik gerçekten açık" oranı — hepsi 0-100, tam dört eksen.
//
// Denenip ELENENLER (uydurma bir radar üretmemek için):
//   • Kapsam (departman/lokasyon) karşılaştırması: eksen sayısı tenant'a göre değişiyor;
//     18 departmanlı bir müşteride radar okunamaz hâle gelir.
//   • Hazırlık skoru: TEK bir sayı + eylem listesi döndürüyor, boyutları yok.
//   • `.distribution` metrikleri: bunlar YOĞUNLAŞMA (en büyük grubun payı), tam dağılım
//     vermiyorlar — radar/halka için gereken parçalar üründe yok.
//
// ═══════════ NE SÖYLER ═══════════
// "Analitiğimin hangi katmanları gerçekten açık?" Bu, hazırlık skorunun kırılımı gibidir ama
// tek sayı yerine şeklini gösterir: örneğin Öngörü ekseninin sıfıra çökmesi, o katmanın kadro
// ve geçmiş eşiklerine takıldığını bir bakışta anlatır.

const Wrap = styled.div`
  display: flex;
  flex-direction: column;
  min-width: 0;
`;

const Label = styled.span`
  font-family: ${({ theme }) => theme.metrics.font.family};
  font-size: ${({ theme }) => theme.metrics.font.size.sm};
  font-weight: ${({ theme }) => theme.metrics.font.weight.semiBold};
  color: ${({ theme }) => theme.colors.text.muted};
`;

const Sub = styled.span`
  font-family: ${({ theme }) => theme.metrics.font.family};
  font-size: ${({ theme }) => theme.metrics.font.size.xs};
  color: ${({ theme }) => theme.colors.text.muted};
  margin-top: 2px;
`;

const ChartBox = styled.div`
  width: 100%;
  margin-top: 2px;
`;

const TooltipBox = styled.div`
  background: ${({ theme }) => theme.colors.surface.card};
  border: 1px solid ${({ theme }) => theme.colors.surface.border};
  border-radius: ${({ theme }) => theme.metrics.radius.small};
  padding: 6px 10px;
  font-family: ${({ theme }) => theme.metrics.font.family};
  font-size: ${({ theme }) => theme.metrics.font.size.xs};
  color: ${({ theme }) => theme.colors.text.primary};
  box-shadow: 0 2px 8px rgba(${({ theme }) => theme.colors.shadow.rgb}, 0.12);
`;

// Yükseklik SAYI olarak veriliyor: `height="100%"` ebeveynin ölçülmesine bağlıdır ve ölçüm 0
// döndüğünde recharts sessizce hiçbir şey çizmez (bkz. HaTrendChart'taki aynı gerekçe).
const CHART_HEIGHT = 168;

// Katalogdaki katman kodları — backend `HaTier` enum'unun snake_case hâli (HaCatalogDataAccess
// ToSnake ile çeviriyor). Sıra bilinçli: analitik derinliği artan yönde.
const TIERS = ['descriptive', 'diagnostic', 'early_warning', 'predictive'];

export default function HaCoverageRadar({ catalog, gradientId = 'ha-radar-fill' }) {
  const { t } = useTranslation();
  const theme = useTheme();

  const data = useMemo(() => {
    if (!Array.isArray(catalog) || catalog.length === 0) return [];
    return TIERS.map((tier) => {
      const hepsi = catalog.filter((m) => m.tier === tier);
      const acik = hepsi.filter((m) => m.state === 'available').length;
      return {
        tier,
        // Katmanda hiç metrik yoksa oran TANIMSIZDIR; 0 yazmak "hepsi kapalı" der ve yanıltır.
        // Böyle bir eksen radara hiç girmez (aşağıda eleniyor).
        oran: hepsi.length === 0 ? null : Math.round((acik / hepsi.length) * 100),
        acik,
        toplam: hepsi.length,
        etiket: t(`hrAnalytics.tiers.${tier}`),
      };
    }).filter((d) => d.oran !== null);
  }, [catalog, t]);

  // Üç eksenden az bir radar geometrik olarak bir şekil oluşturmaz (iki eksen bir çizgidir).
  if (data.length < 3) return null;

  const toplamAcik = data.reduce((s, d) => s + d.acik, 0);
  const toplamHepsi = data.reduce((s, d) => s + d.toplam, 0);

  return (
    <Wrap data-role="ha-coverage-radar">
      <Label>{t('dashboard.haPanel.coverageTitle')}</Label>
      <Sub>{t('dashboard.haPanel.coverageSub', { open: toplamAcik, total: toplamHepsi })}</Sub>

      <ChartBox>
        <ResponsiveContainer width="100%" height={CHART_HEIGHT}>
          <RadarChart data={data} outerRadius="72%" margin={{ top: 6, right: 6, bottom: 6, left: 6 }}>
            <defs>
              <linearGradient id={gradientId} x1="0" y1="0" x2="1" y2="1">
                <stop offset="0%" stopColor={theme.colors.primary.main} stopOpacity={0.35} />
                <stop offset="100%" stopColor={theme.colors.secondary.main} stopOpacity={0.12} />
              </linearGradient>
            </defs>
            <PolarGrid stroke={theme.colors.surface.border} />
            <PolarAngleAxis
              dataKey="etiket"
              tick={{ fill: theme.colors.text.muted, fontSize: 10 }}
            />
            {/* Ölçek 0-100'e SABİTLENİR: otomatik alan, %90 ile %95 arasındaki farkı
                dramatik gösterip kapsamı olduğundan iyi/kötü okuturdu. */}
            <PolarRadiusAxis domain={[0, 100]} tick={false} axisLine={false} />
            <Tooltip
              content={({ active, payload }) =>
                active && payload && payload.length ? (
                  <TooltipBox>
                    {payload[0].payload.etiket}
                    {' · '}
                    {payload[0].payload.acik}/{payload[0].payload.toplam}
                    {' · %'}
                    {payload[0].payload.oran}
                  </TooltipBox>
                ) : null
              }
            />
            <Radar
              dataKey="oran"
              stroke={theme.colors.primary.main}
              strokeWidth={2}
              fill={`url(#${gradientId})`}
              isAnimationActive={false}
            />
          </RadarChart>
        </ResponsiveContainer>
      </ChartBox>
    </Wrap>
  );
}
